'use client';

import {observer} from 'mobx-react';
import {usePathname} from 'next/navigation';
import type {Dispatch, PropsWithChildren, SetStateAction} from 'react';
import {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from '@/app/i18n/client';
import {useUserStore} from '@/utils/hooks/contextHooks';

import Icon from './base/Icon';
import Modal from './base/Modal';
import Typography from './base/Typography';

type FormDirtyContextData = {
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  setCloseHandler: (save: () => Promise<void>) => void;
  isFormDirty: boolean;
  setSaveHandler: (close: () => Promise<boolean>) => void;
  showModal: boolean;
};

export const FormDirtyContext = createContext({} as FormDirtyContextData);

function FormContextWrapper({children}: PropsWithChildren) {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsFormDirty(false);
    setShowModal(false);
    saveHandler.current = undefined;
    closeHandler.current = undefined;
  }, [pathname]);

  const saveHandler = useRef<() => Promise<boolean>>();
  const closeHandler = useRef<() => Promise<void>>();

  const userStore = useUserStore();
  const {t} = useTranslation(userStore?.user?.preference?.language || 'en', 'common');

  const setSaveHandler = (save: () => Promise<boolean>) => {
    saveHandler.current = save;
  };

  const setCloseHandler = (close: () => Promise<void>) => {
    closeHandler.current = close;
  };
  useEffect(() => {
    if (!isFormDirty) {
      setSaveHandler(undefined);
    }
  }, [isFormDirty]);

  const closeModal = useCallback(async () => {
    await closeHandler.current?.();
    closeHandler.current = undefined;
    setIsFormDirty(false);
    setShowModal(false);
  }, []);

  const closeIcon = useMemo(() => {
    return (
      <Icon
        className="cursor-pointer"
        name="close"
        width={24}
        height={24}
        onClick={() => {
          setShowModal(false);
        }}
      />
    );
  }, []);

  return (
    <FormDirtyContext.Provider value={{setIsFormDirty, setShowModal, setCloseHandler, isFormDirty, setSaveHandler, showModal}}>
      {children}

      <Modal
        title={t('unsavedChanges.title')}
        contentWrapperClass={'flex flex-col text-base-700'}
        isOpen={showModal}
        closeProps={{
          render: closeIcon,
        }}
        actions={[
          {
            text: t('btns.discard'),
            variant: 'danger',
            className: 'bg-red-bright',
            onClick: () => {
              closeModal();
            },
          },
          {
            text: t('btns.save'),
            variant: 'primary',
            onClick: async () => {
              const res = !saveHandler.current || (await saveHandler.current?.());
              if (res) {
                closeModal();
              } else {
                setShowModal(false);
              }
            },
          },
        ]}>
        <Typography>{t('unsavedChanges.description')}</Typography>
      </Modal>
    </FormDirtyContext.Provider>
  );
}

export default observer(FormContextWrapper);
